import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Compass, MapPin, Calendar, Clock, CheckCircle, Truck, Package, Phone, Mail, Building, Lock, Users, Copy, LogOut, UserPlus } from 'lucide-react';
import PilotAvailabilityTab from '../components/PilotAvailabilityTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILOT_ACCREDITATIONS = [
  'VIC LVL1', 'VIC LVL2', 'VIC TC',
  'QLD LVL1', 'QLD LVL2',
  'WA', 'VIC PROB', 'NT PILOT', 'NT ESCORT',
  '4WD SPECIFIC', 'DIESEL', 'PETROL', 'GAS',
];

const PilotDashboard = () => {
  const [pilotEmail, setPilotEmail] = useState(localStorage.getItem('pilot_email') || '');
  const [password, setPassword] = useState('');
  const [isAuthed, setIsAuthed] = useState(localStorage.getItem('pilot_authed') === '1');
  const [isAgent, setIsAgent] = useState(localStorage.getItem('pilot_is_agent') === '1');
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('jobs');

  // Agent sub-pilot state
  const [subPilots, setSubPilots] = useState([]);
  const [showAddSub, setShowAddSub] = useState(false);
  const [subForm, setSubForm] = useState({ name: '', sub_pilot_email: '', phone: '', company_name: '', password: '' });
  const [subCreds, setSubCreds] = useState(null); // credentials to display after creation
  const [creatingSub, setCreatingSub] = useState(false);
  const [subEdit, setSubEdit] = useState(null); // { sub, form, docs, docsLoading, newDocType, newDocExpiry }
  const [subEditSaving, setSubEditSaving] = useState(false);
  const [subEditErr, setSubEditErr] = useState('');
  const [subAvail, setSubAvail] = useState(null); // { sub, base_suburb, base_state, notes, accreditations, vehicle_type }
  const [subAvailSaving, setSubAvailSaving] = useState(false);
  const [subAvailErr, setSubAvailErr] = useState('');

  // Join-an-agent state (for regular pilots)
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  // Incoming join requests (for agents)
  const [joinRequests, setJoinRequests] = useState([]);

  // Change-password modal state
  const [showChangePw, setShowChangePw] = useState(false);
  const [changePw, setChangePw] = useState({ current: '', next: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);

  const fetchDashboard = async (email) => {
    const e = email || pilotEmail;
    if (!e) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pilot-dashboard/${e}`);
      setData(res.data);
      localStorage.setItem('pilot_email', e);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load dashboard');
      setData(null);
    } finally { setLoading(false); }
  };

  const handleLogin = async () => {
    if (!pilotEmail || !password) { toast.error('Enter your email or Pilot ID and password'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pilots/auth/login`, { identifier: pilotEmail, password });
      // Server resolves email even when the pilot logged in with a Pilot ID (P00011).
      const resolvedEmail = res.data.email || pilotEmail;
      localStorage.setItem('pilot_email', resolvedEmail);
      localStorage.setItem('pilot_authed', '1');
      localStorage.setItem('pilot_is_agent', res.data.is_agent ? '1' : '0');
      setPilotEmail(resolvedEmail);
      setIsAuthed(true);
      setIsAgent(!!res.data.is_agent);
      setPassword('');
      await fetchDashboard(resolvedEmail);
      if (res.data.is_agent) fetchSubPilots(res.data.registrations?.[0]?.id);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Login failed';
      toast.error(detail);
      if (detail.includes('No password set')) setShowSetPassword(true);
    } finally { setLoading(false); }
  };

  const handleSetPassword = async () => {
    if (!pilotEmail || !newPassword) { toast.error('Email and new password required'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      await axios.post(`${API}/pilots/auth/set-password`, { email: pilotEmail, password: newPassword });
      toast.success('Password set — please log in');
      setShowSetPassword(false);
      setPassword(newPassword);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('pilot_authed');
    localStorage.removeItem('pilot_is_agent');
    setIsAuthed(false);
    setIsAgent(false);
    setData(null);
    setPassword('');
  };

  const fetchSubPilots = async (agentRegId) => {
    if (!agentRegId) return;
    try {
      const res = await axios.get(`${API}/pilots/agent/${agentRegId}/sub-pilots`);
      setSubPilots(res.data || []);
    } catch { /* ignore */ }
  };

  const handleAddSubPilot = async () => {
    if (!data?.pilot?.id) return;
    if (!subForm.name) {
      toast.error('Pilot name is required'); return;
    }
    if (subForm.password && subForm.password.length < 8) {
      toast.error('Password must be at least 8 characters'); return;
    }
    setCreatingSub(true);
    try {
      const res = await axios.post(`${API}/pilots/agent/register-sub-pilot`, {
        agent_email: pilotEmail,
        agent_registration_id: data.pilot.id,
        ...subForm,
      });
      setSubCreds(res.data.credentials);   // show credentials modal to agent
      setSubForm({ name: '', sub_pilot_email: '', phone: '', company_name: '', password: '' });
      setShowAddSub(false);
      fetchSubPilots(data.pilot.id);
      setIsAgent(true);
      localStorage.setItem('pilot_is_agent', '1');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register sub-pilot');
    } finally { setCreatingSub(false); }
  };

  const openSubEdit = async (sub) => {
    setSubEditErr('');
    setSubEdit({
      sub,
      form: {
        name: sub.name || '',
        phone: sub.phone || '',
        email: sub.email || '',
        company_name: sub.company_name || '',
        abn: sub.abn || '',
        address: sub.address || '',
        bank_account_name: sub.bank_account_name || '',
        bank_bsb: sub.bank_bsb || '',
        bank_account_number: sub.bank_account_number || '',
        is_steerer: !!sub.is_steerer,
        notes: sub.notes || '',
        password: '',
      },
      docs: [],
      docsLoading: true,
      newDocType: '',
      newDocExpiry: '',
    });
    try {
      const res = await axios.get(
        `${API}/pilots/agent/sub-pilots/${sub.id}/documents`,
        { params: { agent_email: pilotEmail, agent_registration_id: data.pilot.id } },
      );
      setSubEdit((prev) => prev ? { ...prev, docs: res.data || [], docsLoading: false } : prev);
    } catch (e) {
      setSubEdit((prev) => prev ? { ...prev, docs: [], docsLoading: false } : prev);
    }
  };

  const handleSubEditSave = async () => {
    if (!subEdit) return;
    const f = subEdit.form;
    if (!f.name) { setSubEditErr('Name is required'); return; }
    if (f.password && f.password.length < 8) { setSubEditErr('Password must be at least 8 characters'); return; }
    setSubEditSaving(true);
    setSubEditErr('');
    try {
      const payload = { agent_email: pilotEmail, agent_registration_id: data.pilot.id, ...f };
      if (!payload.password) delete payload.password;
      await axios.patch(`${API}/pilots/agent/sub-pilots/${subEdit.sub.id}`, payload);
      toast.success('Sub-pilot updated');
      setSubEdit(null);
      fetchSubPilots(data.pilot.id);
    } catch (e) {
      setSubEditErr(e.response?.data?.detail || 'Failed to save');
    } finally { setSubEditSaving(false); }
  };

  const subFileToB64 = (f) => new Promise((r, j) => { const x = new FileReader(); x.onerror = j; x.onload = () => r(x.result); x.readAsDataURL(f); });

  const handleSubUploadDoc = async (file) => {
    if (!subEdit || !file) return;
    const docType = (subEdit.newDocType || '').trim() || 'Accreditation';
    try {
      const dataUrl = await subFileToB64(file);
      const res = await axios.post(
        `${API}/pilots/agent/sub-pilots/${subEdit.sub.id}/documents`,
        {
          agent_email: pilotEmail,
          agent_registration_id: data.pilot.id,
          doc_type: docType,
          expiry_date: subEdit.newDocExpiry || null,
          file_name: file.name,
          file_base64: dataUrl,
        },
      );
      setSubEdit((prev) => prev ? { ...prev, docs: [res.data, ...prev.docs], newDocType: '', newDocExpiry: '' } : prev);
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
  };

  const handleSubDeleteDoc = async (docId) => {
    if (!subEdit) return;
    try {
      await axios.delete(`${API}/pilots/agent/sub-pilots/${subEdit.sub.id}/documents/${docId}`, {
        params: { agent_email: pilotEmail, agent_registration_id: data.pilot.id },
      });
      setSubEdit((prev) => prev ? { ...prev, docs: prev.docs.filter((d) => d.id !== docId) } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openSubAvail = (sub) => {
    setSubAvailErr('');
    setSubAvail({ sub, base_suburb: '', base_state: 'NSW', notes: '', accreditations: [], vehicle_type: '' });
  };

  const handleCreateSubAvailability = async () => {
    if (!subAvail) return;
    if (!subAvail.base_suburb.trim() || !subAvail.base_state.trim()) {
      setSubAvailErr('Suburb and state are required'); return;
    }
    setSubAvailSaving(true);
    setSubAvailErr('');
    try {
      await axios.post(`${API}/pilots/agent/sub-pilots/${subAvail.sub.id}/availability`, {
        agent_email: pilotEmail,
        agent_registration_id: data.pilot.id,
        base_suburb: subAvail.base_suburb.trim(),
        base_state: subAvail.base_state.trim().toUpperCase(),
        notes: subAvail.notes.trim() || null,
        vehicle_type: subAvail.vehicle_type.trim() || null,
        accreditations: Array.isArray(subAvail.accreditations) ? subAvail.accreditations : [],
      });
      toast.success(`${subAvail.sub.name || 'Sub-pilot'} is now on the pilots map`);
      setSubAvail(null);
    } catch (e) {
      setSubAvailErr(e.response?.data?.detail || 'Failed to list sub-pilot');
    } finally { setSubAvailSaving(false); }
  };

  const handleRequestJoinAgent = async () => {
    const code = (joinCode || '').trim().toUpperCase();
    if (!code) { toast.error('Enter an agent Pilot ID'); return; }
    setJoining(true);
    try {
      await axios.post(`${API}/pilots/join-agent-request`, {
        pilot_email: pilotEmail,
        agent_pilot_code: code,
      });
      toast.success(`Request sent to ${code}. They'll approve or reject shortly.`);
      setJoinCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally { setJoining(false); }
  };

  const fetchJoinRequests = async (agentRegId) => {
    if (!agentRegId || !pilotEmail) return;
    try {
      const res = await axios.get(`${API}/pilots/agent/join-requests`, {
        params: { agent_email: pilotEmail, agent_registration_id: agentRegId, status: 'pending' },
      });
      setJoinRequests(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const handleDecideJoinRequest = async (reqId, decision) => {
    try {
      await axios.post(`${API}/pilots/agent/join-requests/${reqId}/decide`, {
        agent_email: pilotEmail,
        agent_registration_id: data.pilot.id,
        decision,
      });
      toast.success(`Request ${decision}d`);
      fetchJoinRequests(data.pilot.id);
      if (decision === 'approve') fetchSubPilots(data.pilot.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update request');
    }
  };

  const handleResetSubPilotPassword = async (subPilot) => {
    if (!data?.pilot?.id) return;
    if (!window.confirm(`Reset password for ${subPilot.name}? A new one will be emailed to them and shown here once.`)) return;
    try {
      const res = await axios.post(`${API}/pilots/agent/reset-sub-pilot-password`, {
        agent_email: pilotEmail,
        agent_registration_id: data.pilot.id,
        sub_pilot_registration_id: subPilot.id,
      });
      setSubCreds(res.data.credentials);
      toast.success('Password reset — new credentials emailed to the pilot');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleChangePassword = async () => {
    if (!changePw.current || !changePw.next || !changePw.confirm) {
      toast.error('All fields are required'); return;
    }
    if (changePw.next.length < 8) {
      toast.error('New password must be at least 8 characters'); return;
    }
    if (changePw.next !== changePw.confirm) {
      toast.error('New password and confirmation do not match'); return;
    }
    setChangingPw(true);
    try {
      await axios.post(`${API}/pilots/auth/set-password`, {
        email: pilotEmail,
        password: changePw.next,
        current_password: changePw.current,
      });
      toast.success('Password changed');
      setShowChangePw(false);
      setChangePw({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  useEffect(() => {
    if (isAuthed && pilotEmail) {
      fetchDashboard();
    }
  }, []);

  useEffect(() => {
    if (data?.pilot?.id) {
      fetchSubPilots(data.pilot.id);
      if (data?.pilot?.is_agent) fetchJoinRequests(data.pilot.id);
    }
  }, [data?.pilot?.id, data?.pilot?.is_agent]);

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString('en-AU'); } catch { return d || ''; } };

  const JobCard = ({ job, showPayment }) => (
    <Card className={`p-5 border-l-4 ${
      job.status === 'assigned' ? 'border-l-blue-500' :
      job.status === 'completed' ? 'border-l-green-500' : 'border-l-gray-300'
    }`} data-testid={`pilot-job-${job.id}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              job.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
              job.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}>{job.status.toUpperCase()}</span>
            <span className="text-xs text-gray-500">{job.service_required}</span>
          </div>
          <h3 className="font-bold text-[#1e3a5f] text-lg">{job.pickup_address} → {job.destination_address}</h3>
        </div>
        {showPayment && job.payment_amount && (
          <p className="text-lg font-bold text-green-700">${job.payment_amount.toLocaleString()}</p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm text-gray-600">
        <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-gray-400" />{job.travel_date} at {job.travel_time}</div>
        <div className="flex items-center gap-1.5"><Package className="w-4 h-4 text-gray-400" />{job.load_type}</div>
        <div className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-gray-400" />H:{job.load_height} W:{job.load_width} L:{job.load_length}</div>
      </div>

      {job.status === 'assigned' && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm">
          <p className="font-semibold text-blue-800 mb-1">Contact Details</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-gray-500">Pickup Contact</p>
              <p>{job.pickup_contact_name} — {job.pickup_contact_phone}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Destination Contact</p>
              <p>{job.destination_contact_name} — {job.destination_contact_phone}</p>
            </div>
          </div>
          {job.permit_number && <p className="mt-1"><strong>Permit:</strong> {job.permit_number}</p>}
          {job.message && <p className="mt-1 italic text-gray-600">"{job.message}"</p>}
        </div>
      )}

      {job.completed_at && (
        <p className="text-xs text-gray-400 mt-2">Completed: {formatDate(job.completed_at)}</p>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-[#1e3a5f] text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <Compass className="w-10 h-10 mx-auto mb-2 text-red-400" />
          <h1 className="text-3xl font-bold">Pilot Dashboard</h1>
          <p className="text-blue-200 mt-1">View your assigned jobs and history</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Login gate */}
        {!isAuthed && (
          <Card className="p-6 max-w-md mx-auto" data-testid="pilot-login-card">
            {!showSetPassword ? (
              <>
                <h2 className="font-bold text-[#1e3a5f] mb-1 flex items-center gap-2"><Lock className="w-4 h-4" /> Pilot login</h2>
                <p className="text-xs text-gray-500 mb-4">Sign in with your <strong>email</strong> or <strong>Pilot ID</strong> (e.g. P00011) and your password.</p>
                <div className="space-y-3">
                  <div>
                    <Label>Email or Pilot ID</Label>
                    <Input type="text" value={pilotEmail} onChange={(e) => setPilotEmail(e.target.value)} placeholder="your@email.com or P00011" data-testid="pilot-login-email" />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} placeholder="Password" data-testid="pilot-login-password" />
                  </div>
                  <Button onClick={handleLogin} disabled={loading} className="w-full bg-[#1e3a5f] text-white" data-testid="pilot-login-btn">
                    {loading ? 'Logging in…' : 'Log in'}
                  </Button>
                  <button className="text-xs text-blue-600 underline w-full text-center" onClick={() => setShowSetPassword(true)} data-testid="pilot-set-password-link">
                    First time here / need to set a password?
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-bold text-[#1e3a5f] mb-1">Set your password</h2>
                <p className="text-xs text-gray-500 mb-4">Enter the email on your pilot registration to set a password. Minimum 8 characters.</p>
                <div className="space-y-3">
                  <div>
                    <Label>Registered email</Label>
                    <Input type="email" value={pilotEmail} onChange={(e) => setPilotEmail(e.target.value)} data-testid="pilot-setpw-email" />
                  </div>
                  <div>
                    <Label>New password (min 8 chars)</Label>
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="pilot-setpw-new" />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowSetPassword(false)} className="flex-1">Back</Button>
                    <Button onClick={handleSetPassword} className="flex-1 bg-[#1e3a5f] text-white" data-testid="pilot-setpw-save">Set password</Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        )}

        {/* Dashboard */}
        {isAuthed && data && (
          <>
            {/* Pilot Info */}
            <Card className="p-5 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-bold text-[#1e3a5f] flex items-center gap-2">
                    {data.pilot.name}
                    {(isAgent || subPilots.length > 0) && <span className="text-[10px] font-semibold bg-purple-600 text-white px-2 py-0.5 rounded uppercase tracking-wider" data-testid="pilot-agent-badge">Agent</span>}
                  </h2>
                  <p className="text-sm text-gray-600">{data.pilot.company_name}</p>
                  <p className="text-xs text-gray-400">{data.pilot.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    data.pilot.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>{data.pilot.status}</span>
                  <Button variant="outline" size="sm" onClick={() => setShowChangePw(true)} className="text-purple-700 border-purple-400 hover:bg-purple-50" data-testid="pilot-change-pw-btn">
                    <Lock className="w-3.5 h-3.5 mr-1" /> Change password
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="pilot-logout-btn">
                    <LogOut className="w-3.5 h-3.5 mr-1" /> Log out
                  </Button>
                </div>
              </div>
            </Card>

            {/* Join an agent (only for pilots who aren't agents and aren't already under one) */}
            {!isAgent && !data.pilot.agent_id && (
              <Card className="p-4 mb-4 border-l-4 border-l-purple-500" data-testid="pilot-join-agent-card">
                <h3 className="text-sm font-bold text-purple-700 mb-1 uppercase tracking-wider">Work under an agent?</h3>
                <p className="text-xs text-gray-500 mb-3">
                  If you regularly work under another pilot / agency, enter their <strong>Pilot ID</strong> (e.g. P00011) to request being listed under them. They&apos;ll approve the link from their dashboard.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Input
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 20))}
                    placeholder="P00011"
                    className="font-mono max-w-[180px]"
                    data-testid="pilot-join-code"
                  />
                  <Button
                    onClick={handleRequestJoinAgent}
                    disabled={joining}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="pilot-join-submit"
                  >
                    {joining ? 'Sending…' : 'Request to join'}
                  </Button>
                </div>
              </Card>
            )}
            {!isAgent && data.pilot.agent_id && (
              <Card className="p-3 mb-4 bg-purple-50 border border-purple-200 text-sm" data-testid="pilot-under-agent">
                <span className="text-purple-800">You are currently listed under an agent. Ask them to release you if you&apos;d like to switch.</span>
              </Card>
            )}

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setActiveTab('jobs')}
                className={`py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${
                  activeTab === 'jobs' ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="pilot-tab-jobs"
              >
                Active Jobs ({data.assigned_jobs.length})
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${
                  activeTab === 'history' ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="pilot-tab-history"
              >
                History ({data.completed_jobs.length})
              </button>
              <button
                onClick={() => setActiveTab('availability')}
                className={`py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${
                  activeTab === 'availability' ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="pilot-tab-availability"
              >
                My Availability
              </button>
              <button
                onClick={() => setActiveTab('team')}
                className={`py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${
                  activeTab === 'team' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="pilot-tab-team"
              >
                <Users className="w-3.5 h-3.5 inline-block mr-1" />
                My Team ({subPilots.length})
              </button>
            </div>

            {/* Active Jobs */}
            {activeTab === 'jobs' && (
              <div className="space-y-4">
                {data.assigned_jobs.length === 0 ? (
                  <Card className="p-12 text-center">
                    <Compass className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Active Jobs</h3>
                    <p className="text-gray-500">When you're assigned a job, it will appear here with all the details you need.</p>
                  </Card>
                ) : data.assigned_jobs.map(job => <JobCard key={job.id} job={job} showPayment={false} />)}
              </div>
            )}

            {/* History */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                {data.completed_jobs.length === 0 ? (
                  <Card className="p-12 text-center">
                    <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Completed Jobs</h3>
                    <p className="text-gray-500">Completed jobs will appear here for your records.</p>
                  </Card>
                ) : data.completed_jobs.map(job => <JobCard key={job.id} job={job} showPayment={true} />)}
              </div>
            )}

            {/* My Availability */}
            {activeTab === 'availability' && (
              <PilotAvailabilityTab pilotEmail={data.pilot.email} />
            )}

            {/* Team (agent) */}
            {activeTab === 'team' && (
              <div className="space-y-4">
                {joinRequests.length > 0 && (
                  <Card className="p-4 border-l-4 border-l-purple-500" data-testid="agent-pending-requests">
                    <h3 className="text-sm font-bold text-purple-700 mb-1 uppercase tracking-wider">
                      Pilots wanting to join you ({joinRequests.length})
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">Approve to list them under your agent account. Their profile stays their own.</p>
                    <div className="space-y-2">
                      {joinRequests.map((r) => (
                        <div key={r.id} className="flex items-center justify-between flex-wrap gap-2 border rounded p-2">
                          <div>
                            <span className="font-mono text-[11px] bg-[#1e3a5f] text-white px-2 py-0.5 rounded mr-2">{r.pilot_code || 'P-—'}</span>
                            <span className="font-semibold text-[#1e3a5f]">{r.pilot_name}</span>
                            <span className="text-xs text-gray-500 ml-2">· {r.pilot_email}</span>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleDecideJoinRequest(r.id, 'approve')}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`join-approve-${r.id}`}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDecideJoinRequest(r.id, 'reject')}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              data-testid={`join-reject-${r.id}`}
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-lg font-bold text-[#1e3a5f]">Sub-pilots you manage</h3>
                    <p className="text-xs text-gray-500">Each sub-pilot gets their own login and can access their own dashboard. You and the admin can also manage them.</p>
                  </div>
                  <Button onClick={() => setShowAddSub(true)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="pilot-add-sub-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Register new pilot
                  </Button>
                </div>

                {subPilots.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No sub-pilots yet. Register one to get started.</p>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {subPilots.map(sp => (
                      <Card key={sp.id} className="p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`sub-pilot-${sp.id}`}>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#1e3a5f] text-white font-mono">
                              {sp.pilot_code || 'P-—'}
                            </span>
                            <span className="font-semibold text-[#1e3a5f]">{sp.name}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold ${sp.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{sp.status || 'pending'}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{sp.email} · {sp.phone}</p>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSubEdit(sp)}
                            className="text-blue-700 border-blue-400 hover:bg-blue-50"
                            data-testid={`sub-edit-${sp.id}`}
                          >
                            Edit profile
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSubAvail(sp)}
                            className="text-emerald-700 border-emerald-400 hover:bg-emerald-50"
                            disabled={sp.status !== 'approved'}
                            title={sp.status !== 'approved' ? 'Sub-pilot must be approved by admin first' : 'List location on the pilots map'}
                            data-testid={`sub-list-avail-${sp.id}`}
                          >
                            List location
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetSubPilotPassword(sp)}
                            className="text-purple-700 border-purple-400 hover:bg-purple-50"
                            data-testid={`reset-pw-${sp.id}`}
                          >
                            <Lock className="w-3.5 h-3.5 mr-1" /> Reset password
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Register sub-pilot modal */}
            {showAddSub && (
              <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAddSub(false)} data-testid="add-sub-backdrop">
                <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2"><UserPlus className="w-5 h-5 text-purple-600" /> Register a pilot under you</h3>
                  <p className="text-sm text-gray-600 mb-4">Only <strong>name</strong> is required. A pilot code (P#####) and password are auto-generated; if you provide an email we&apos;ll email the login to them.</p>
                  <div className="space-y-3">
                    <div>
                      <Label>Pilot name *</Label>
                      <Input value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} placeholder="Jane Doe" data-testid="sub-name" />
                    </div>
                    <div>
                      <Label>Sub-pilot email <span className="text-gray-400 text-xs">(optional)</span></Label>
                      <Input type="email" value={subForm.sub_pilot_email} onChange={(e) => setSubForm({ ...subForm, sub_pilot_email: e.target.value })} placeholder="jane@example.com — leave blank if unknown" data-testid="sub-email" />
                    </div>
                    <div>
                      <Label>Phone <span className="text-gray-400 text-xs">(optional)</span></Label>
                      <Input value={subForm.phone} onChange={(e) => setSubForm({ ...subForm, phone: e.target.value })} placeholder="0400 000 000" data-testid="sub-phone" />
                    </div>
                    <div>
                      <Label>Company (optional)</Label>
                      <Input value={subForm.company_name} onChange={(e) => setSubForm({ ...subForm, company_name: e.target.value })} placeholder="Uses your company name if blank" data-testid="sub-company" />
                    </div>
                    <div>
                      <Label>Password <span className="text-gray-400 text-xs">(optional — auto-generated if blank)</span></Label>
                      <Input
                        type="text"
                        value={subForm.password}
                        onChange={(e) => setSubForm({ ...subForm, password: e.target.value })}
                        placeholder="Choose one or leave blank"
                        data-testid="sub-password"
                        autoComplete="new-password"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">Min 8 characters. Share it manually with your pilot.</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowAddSub(false)} className="flex-1">Cancel</Button>
                      <Button onClick={handleAddSubPilot} disabled={creatingSub} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" data-testid="sub-submit">
                        {creatingSub ? 'Registering…' : 'Register pilot'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Change password modal */}
            {showChangePw && (
              <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowChangePw(false)} data-testid="change-pw-backdrop">
                <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2"><Lock className="w-5 h-5" /> Change your password</h3>
                  <p className="text-xs text-gray-500 mb-4">Enter your current password and choose a new one. Minimum 8 characters.</p>
                  <div className="space-y-3">
                    <div>
                      <Label>Current password</Label>
                      <Input type="password" value={changePw.current} onChange={(e) => setChangePw({ ...changePw, current: e.target.value })} data-testid="change-pw-current" />
                    </div>
                    <div>
                      <Label>New password</Label>
                      <Input type="password" value={changePw.next} onChange={(e) => setChangePw({ ...changePw, next: e.target.value })} data-testid="change-pw-new" />
                    </div>
                    <div>
                      <Label>Confirm new password</Label>
                      <Input type="password" value={changePw.confirm} onChange={(e) => setChangePw({ ...changePw, confirm: e.target.value })} data-testid="change-pw-confirm" />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowChangePw(false)} className="flex-1">Cancel</Button>
                      <Button onClick={handleChangePassword} disabled={changingPw} className="flex-1 bg-[#1e3a5f] text-white" data-testid="change-pw-submit">
                        {changingPw ? 'Saving…' : 'Save new password'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Credentials modal (shown once after sub-pilot creation) */}
            {subCreds && (
              <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" data-testid="sub-creds-backdrop">
                <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">Share these credentials with your pilot</h3>
                  <p className="text-sm text-red-600 mb-4">This password is shown once — copy it now.</p>
                  <div className="space-y-2 mb-4">
                    {subCreds.pilot_code && (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded font-mono text-sm">
                        <div className="text-[10px] text-purple-700 uppercase font-semibold mb-1">Pilot ID</div>
                        <div className="flex items-center justify-between gap-2">
                          <span data-testid="sub-cred-code">{subCreds.pilot_code}</span>
                          <button onClick={() => { navigator.clipboard.writeText(subCreds.pilot_code); toast.success('Pilot ID copied'); }} className="text-purple-700" title="Copy">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                    {subCreds.email_is_placeholder ? (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                        No email was provided — the sub-pilot cannot log in yet. Give an admin the pilot ID above and ask them to add the email later, or edit the profile from the dashboard.
                      </div>
                    ) : (
                      <div className="p-3 bg-slate-50 rounded border font-mono text-sm">
                        <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Email</div>
                        <div className="flex items-center justify-between gap-2">
                          <span data-testid="sub-cred-email">{subCreds.email}</span>
                          <button onClick={() => { navigator.clipboard.writeText(subCreds.email); toast.success('Email copied'); }} className="text-blue-600" title="Copy">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="p-3 bg-slate-50 rounded border font-mono text-sm">
                      <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Password</div>
                      <div className="flex items-center justify-between gap-2">
                        <span data-testid="sub-cred-password">{subCreds.password}</span>
                        <button onClick={() => { navigator.clipboard.writeText(subCreds.password); toast.success('Password copied'); }} className="text-blue-600" title="Copy">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => setSubCreds(null)} className="w-full bg-[#1e3a5f] text-white" data-testid="sub-creds-close">Done — I&apos;ve saved them</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sub-pilot Edit Profile + Accreditations modal */}
      {subEdit && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => !subEditSaving && setSubEdit(null)} data-testid="sub-edit-backdrop">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">Edit sub-pilot</h3>
            <p className="text-xs text-gray-500 mb-4 font-mono">{subEdit.sub.pilot_code} · {subEdit.sub.email}</p>

            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Full name *</Label><Input value={subEdit.form.name} onChange={(e) => setSubEdit({ ...subEdit, form: { ...subEdit.form, name: e.target.value } })} data-testid="sub-edit-name" /></div>
                <div><Label>Company</Label><Input value={subEdit.form.company_name} onChange={(e) => setSubEdit({ ...subEdit, form: { ...subEdit.form, company_name: e.target.value } })} data-testid="sub-edit-company" /></div>
                <div><Label>Phone</Label><Input value={subEdit.form.phone} onChange={(e) => setSubEdit({ ...subEdit, form: { ...subEdit.form, phone: e.target.value } })} data-testid="sub-edit-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={subEdit.form.email} onChange={(e) => setSubEdit({ ...subEdit, form: { ...subEdit.form, email: e.target.value } })} data-testid="sub-edit-email" /></div>
                <div><Label>ABN</Label><Input value={subEdit.form.abn} onChange={(e) => setSubEdit({ ...subEdit, form: { ...subEdit.form, abn: e.target.value } })} data-testid="sub-edit-abn" /></div>
                <div className="sm:col-span-2"><Label>Address</Label><Input value={subEdit.form.address} onChange={(e) => setSubEdit({ ...subEdit, form: { ...subEdit.form, address: e.target.value } })} /></div>
              </div>

              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Change password (optional)</p>
                <Input
                  type="text"
                  value={subEdit.form.password}
                  onChange={(e) => setSubEdit({ ...subEdit, form: { ...subEdit.form, password: e.target.value } })}
                  placeholder="Leave blank to keep current password"
                  data-testid="sub-edit-password"
                  autoComplete="new-password"
                />
              </div>

              <div className="pt-3 border-t border-gray-100">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={subEdit.form.is_steerer} onChange={(e) => setSubEdit({ ...subEdit, form: { ...subEdit.form, is_steerer: e.target.checked } })} className="w-4 h-4 accent-[#1e3a5f]" data-testid="sub-edit-is-steerer" />
                  Offers steerer service
                </label>
              </div>

              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Accreditations &amp; documents</p>
                <div className="space-y-2">
                  {subEdit.docsLoading && <p className="text-xs text-gray-500">Loading…</p>}
                  {!subEdit.docsLoading && subEdit.docs.length === 0 && <p className="text-xs text-gray-500">No documents uploaded yet.</p>}
                  {subEdit.docs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 border rounded p-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[#1e3a5f] truncate">{d.doc_type}</p>
                        <p className="text-[11px] text-gray-500 truncate">{d.file_name}{d.expiry_date ? ` · expires ${d.expiry_date}` : ''}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleSubDeleteDoc(d.id)} className="text-red-600 border-red-300" data-testid={`sub-doc-del-${d.id}`}>Delete</Button>
                    </div>
                  ))}
                  <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
                    <div className="grid sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Document type</Label>
                        <Input value={subEdit.newDocType} onChange={(e) => setSubEdit({ ...subEdit, newDocType: e.target.value })} placeholder="e.g. Pilot certificate" data-testid="sub-doc-type" />
                      </div>
                      <div>
                        <Label className="text-xs">Expiry (optional)</Label>
                        <Input type="date" value={subEdit.newDocExpiry} onChange={(e) => setSubEdit({ ...subEdit, newDocExpiry: e.target.value })} data-testid="sub-doc-expiry" />
                      </div>
                    </div>
                    <label className="block">
                      <span className="text-xs text-gray-600 block mb-1">Upload file (PDF / JPG / PNG · max 10 MB)</span>
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#1e3a5f] file:text-white file:font-semibold hover:file:bg-[#254d7f] cursor-pointer"
                        data-testid="sub-doc-file"
                        onChange={async (e) => {
                          const f = e.target.files && e.target.files[0];
                          if (!f) return;
                          await handleSubUploadDoc(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {subEditErr && <p className="text-sm text-red-600" data-testid="sub-edit-error">{subEditErr}</p>}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setSubEdit(null)} className="flex-1" disabled={subEditSaving} data-testid="sub-edit-cancel">Cancel</Button>
                <Button onClick={handleSubEditSave} disabled={subEditSaving} className="flex-1 bg-[#1e3a5f] hover:bg-[#254d7f] text-white" data-testid="sub-edit-save">
                  {subEditSaving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub-pilot List Availability modal */}
      {subAvail && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => !subAvailSaving && setSubAvail(null)} data-testid="sub-avail-backdrop">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">List <span className="font-mono">{subAvail.sub.pilot_code}</span> on the pilots map</h3>
            <p className="text-xs text-gray-500 mb-4">{subAvail.sub.name}</p>

            <div className="space-y-3">
              <div className="grid grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label>Base suburb *</Label>
                  <Input value={subAvail.base_suburb} onChange={(e) => setSubAvail({ ...subAvail, base_suburb: e.target.value })} placeholder="e.g. Wagga Wagga" data-testid="sub-avail-suburb" />
                </div>
                <div>
                  <Label>State *</Label>
                  <select value={subAvail.base_state} onChange={(e) => setSubAvail({ ...subAvail, base_state: e.target.value })} className="w-full h-10 border rounded-md px-3 text-sm bg-white" data-testid="sub-avail-state">
                    {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Vehicle type <span className="text-gray-400 text-xs">(optional)</span></Label>
                <Input value={subAvail.vehicle_type} onChange={(e) => setSubAvail({ ...subAvail, vehicle_type: e.target.value })} placeholder="e.g. Purpose-built pilot vehicle" data-testid="sub-avail-vehicle" />
              </div>
              <div>
                <Label>Accreditations <span className="text-gray-400 text-xs">(pick all that apply)</span></Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="sub-avail-accs">
                  {PILOT_ACCREDITATIONS.map((a) => {
                    const active = (subAvail.accreditations || []).includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => setSubAvail({
                          ...subAvail,
                          accreditations: active
                            ? subAvail.accreditations.filter((x) => x !== a)
                            : [...(subAvail.accreditations || []), a],
                        })}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          active
                            ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#1e3a5f]'
                        }`}
                        data-testid={`sub-avail-accs-${a.replace(/\s+/g, '-')}`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={subAvail.notes} onChange={(e) => setSubAvail({ ...subAvail, notes: e.target.value })} placeholder="Any other info visible on the map card" data-testid="sub-avail-notes" />
              </div>
              {subAvailErr && <p className="text-sm text-red-600" data-testid="sub-avail-error">{subAvailErr}</p>}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setSubAvail(null)} className="flex-1" disabled={subAvailSaving} data-testid="sub-avail-cancel">Cancel</Button>
                <Button onClick={handleCreateSubAvailability} disabled={subAvailSaving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sub-avail-submit">
                  {subAvailSaving ? 'Listing…' : 'List on map'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PilotDashboard;
